import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCategories } from '../../context/CategoriesContext';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import './CollectionsGrid.css';

const CollectionsGrid = () => {
  const { categories, loading: categoriesLoading } = useCategories();
  const [isMobile, setIsMobile] = useState(false);
  
  // Filter to show only the 4 specific collections on home page
  const homePageCollections = categories.filter(category => 
    ['shirts', 'blazer', 'ethnic-sets', 'pants'].includes(category.id)
  );

  // Detect mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get responsive image sources
  const getImageSources = (category) => {
    if (category.imageURLs?.optimized) {
      const sources = [];
      const sizeOrder = ['480w', '768w', '1080w', '1440w', '1920w'];
      
      sizeOrder.forEach(size => {
        if (category.imageURLs.optimized[size]?.url) {
          sources.push({
            srcset: category.imageURLs.optimized[size].url,
            media: size === '480w' ? '(max-width: 480px)' :
                   size === '768w' ? '(max-width: 768px)' :
                   size === '1080w' ? '(max-width: 1080px)' :
                   size === '1440w' ? '(max-width: 1440px)' :
                   '(min-width: 1441px)'
          });
        }
      });
      
      return sources;
    }
    return [];
  };

  return (
    <section className="collectionsgrid-section">
      <div className="collectionsgrid-container">
        {/* Section Header */}
        <div className="collectionsgrid-header">
          <div className="collectionsgrid-label">
            <div className="collectionsgrid-label-line"></div>
            <span className="collectionsgrid-label-text">Browse Our Range</span>
            <div className="collectionsgrid-label-line"></div>
          </div>
          <h2 className="collectionsgrid-title">All Collections</h2>
          <p className="collectionsgrid-subtitle">
            Explore our complete range of bespoke menswear crafted to perfection
          </p>
        </div>

        {/* Collections Grid/Carousel */}
        {isMobile && !categoriesLoading && homePageCollections.length > 0 ? (
          // Mobile Carousel
          <Swiper
            modules={[Navigation, Pagination]}
            spaceBetween={16}
            slidesPerView={1.2}
            centeredSlides={false}
            pagination={{ clickable: true }}
            className="collectionsgrid-carousel"
          >
            {homePageCollections.map((category) => {
              const sources = getImageSources(category);
              const fallbackUrl = category.imageURLs?.original?.url || '/images/fallbacks/formal-wear.webp';
              
              return (
                <SwiperSlide key={category.id}>
                  <Link 
                    to={`/collections?category=${category.id}`} 
                    className="collectionsgrid-card collectionsgrid-card-mobile"
                  >
                    <div className="collectionsgrid-img-wrapper">
                      {sources.length > 0 ? (
                        <picture>
                          {sources.map((source, idx) => (
                            <source
                              key={idx}
                              srcSet={source.srcset}
                              media={source.media}
                              type="image/webp"
                            />
                          ))}
                          <img
                            src={fallbackUrl}
                            alt={`${category.categoryName || category.id} Collection`}
                            className="collectionsgrid-img"
                            loading="lazy"
                            onError={(e) => {
                              e.target.src = '/images/fallbacks/formal-wear.webp';
                            }}
                          />
                        </picture>
                      ) : (
                        <img
                          src={fallbackUrl}
                          alt={`${category.categoryName || category.id} Collection`}
                          className="collectionsgrid-img"
                          loading="lazy"
                          onError={(e) => {
                            e.target.src = '/images/fallbacks/formal-wear.webp';
                          }}
                        />
                      )}
                      <div className="collectionsgrid-overlay-mobile">
                        <h3 className="collectionsgrid-category-name">{category.categoryName || category.id}</h3>
                      </div>
                    </div>
                  </Link>
                </SwiperSlide>
              );
            })}
          </Swiper>
        ) : (
          // Desktop/Tablet Grid
          <div className="collectionsgrid-grid">
            {categoriesLoading ? (
              // Loading skeleton
              Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="collectionsgrid-card collectionsgrid-card-loading">
                  <div className="collectionsgrid-img-wrapper">
                    <div className="collectionsgrid-skeleton"></div>
                    <div className="collectionsgrid-skeleton-overlay">
                      <div className="collectionsgrid-skeleton-text"></div>
                      <div className="collectionsgrid-skeleton-text-short"></div>
                    </div>
                  </div>
                </div>
              ))
            ) : homePageCollections.length > 0 ? (
              // Render specific 4 collections from database
              homePageCollections.map((category, index) => {
                const sources = getImageSources(category);
                const fallbackUrl = category.imageURLs?.original?.url || '/images/fallbacks/formal-wear.webp';
                
                return (
                  <Link 
                    key={category.id} 
                    to={`/collections?category=${category.id}`} 
                    className="collectionsgrid-card"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="collectionsgrid-img-wrapper">
                      {sources.length > 0 ? (
                        <picture>
                          {sources.map((source, idx) => (
                            <source
                              key={idx}
                              srcSet={source.srcset}
                              media={source.media}
                              type="image/webp"
                              sizes="(max-width: 600px) 100vw, (max-width: 1200px) 50vw, 25vw"
                            />
                          ))}
                          <img
                            src={fallbackUrl}
                            alt={`${category.categoryName || category.id} Collection`}
                            className="collectionsgrid-img"
                            loading="lazy"
                            sizes="(max-width: 600px) 100vw, (max-width: 1200px) 50vw, 25vw"
                            onError={(e) => {
                              e.target.src = '/images/fallbacks/formal-wear.webp';
                            }}
                          />
                        </picture>
                      ) : (
                        <img
                          src={fallbackUrl}
                          alt={`${category.categoryName || category.id} Collection`}
                          className="collectionsgrid-img"
                          loading="lazy"
                          sizes="(max-width: 600px) 100vw, (max-width: 1200px) 50vw, 25vw"
                          onError={(e) => {
                            e.target.src = '/images/fallbacks/formal-wear.webp';
                          }}
                        />
                      )}
                      <div className="collectionsgrid-overlay">
                        <h3 className="collectionsgrid-category-name">{category.categoryName || category.id}</h3>
                      </div>
                    </div>
                  </Link>
                );
              })
            ) : (
            // Beautiful "Coming Soon" section when no categories
            <div className="collectionsgrid-empty-state">
              <div className="collectionsgrid-empty-icon">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" fill="#D6A464"/>
                  <path d="M19 15L19.74 17.74L22.5 18.5L19.74 19.26L19 22L18.26 19.26L15.5 18.5L18.26 17.74L19 15Z" fill="#D6A464"/>
                  <path d="M5 6L5.37 7.37L6.74 7.74L5.37 8.11L5 9.48L4.63 8.11L3.26 7.74L4.63 7.37L5 6Z" fill="#D6A464"/>
                </svg>
              </div>
              <h2 className="collectionsgrid-empty-title">Something Amazing is Coming</h2>
              <p className="collectionsgrid-empty-text">
                We're crafting something extraordinary for our collections. 
                Our artisans are working tirelessly to bring you the finest quality pieces.
              </p>
              <div className="collectionsgrid-empty-features">
                <div className="collectionsgrid-feature-item">
                  <div className="collectionsgrid-feature-icon">✨</div>
                  <span>Premium Quality</span>
                </div>
                <div className="collectionsgrid-feature-item">
                  <div className="collectionsgrid-feature-icon">🎨</div>
                  <span>Handcrafted</span>
                </div>
                <div className="collectionsgrid-feature-item">
                  <div className="collectionsgrid-feature-icon">💎</div>
                  <span>Exclusive Design</span>
                </div>
              </div>
              <div className="collectionsgrid-empty-actions">
                <Link to="/collections" className="collectionsgrid-browse-btn">
                  Browse Collections
                </Link>
              </div>
            </div>
            )}
          </div>
        )}

        {/* View All Link - Compact Pill Button */}
        {!categoriesLoading && homePageCollections.length > 0 && (
          <div className="collectionsgrid-footer">
            <Link to="/collections" className="collectionsgrid-viewall-link">
              Explore All Collections →
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default CollectionsGrid;

